import * as R from 'ramda';
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useOrderItemGridUpdate } from '../../../../hooks/useOrderItemGridUpdate';
import { SavedStackItem } from '../../../../interfaces/use-order-item-stack.types';
import { GridStoreApi, createGridStore } from './gridStore';
import { buildCombinationKey } from './gridStore.utils';

const GridStoreContext = createContext<GridStoreApi | null>(null);

const useGridStoreProvider = (onAfterSave?: OnAfterSave) => {
  const [store] = useState<GridStoreApi>(() =>
    createGridStore({
      persistStackItems: async () => undefined,
    }),
  );

  const afterUpdate = async (savedItems: SavedStackItem[]) => {
    const grouped = R.groupBy(
      (savedItem: SavedStackItem) =>
        buildCombinationKey({
          productColorId: savedItem.productColorId,
          availability: savedItem.availability,
          deliveryWindowId: savedItem.deliveryWindowId,
          orderId: savedItem.orderId,
        }),
      savedItems,
    );

    R.toPairs(grouped).forEach(([combinationKey, items]) => {
      if (!combinationKey || !items || items.length === 0) {
        return;
      }
      store.getState().reconcile({ combinationKey, savedItems: items });
      store.notifyCombinationSaved(combinationKey, items);
    });

    await onAfterSave?.(savedItems);
  };

  const { updateOrderItems } = useOrderItemGridUpdate({ afterUpdate });

  useEffect(() => {
    store.setPersistStackItems(updateOrderItems);
  }, [store, updateOrderItems]);

  useEffect(() => {
    return () => {
      store.flushAndCancel();
    };
  }, [store]);

  return store;
};

type OnAfterSave = (savedItems: SavedStackItem[]) => void | Promise<void>;

interface GridStoreProviderProps {
  children: ReactNode;
  onAfterSave?: OnAfterSave;
}

export const GridStoreProvider = ({ children, onAfterSave }: GridStoreProviderProps) => {
  const store = useGridStoreProvider(onAfterSave);

  return <GridStoreContext.Provider value={store}>{children}</GridStoreContext.Provider>;
};

export const useGridStoreApi = (): GridStoreApi => {
  const api = useContext(GridStoreContext);
  if (!api) {
    throw new Error('useGridStoreApi must be used within a GridStoreProvider');
  }
  return api;
};

export const useGridStoreApiOptional = (): GridStoreApi | null => useContext(GridStoreContext);
